import moment from "moment";
import {
  ERRANT_REASONS,
  SAMPLE_STATUSES,
  URGENCY_LEVELS,
  getDerivedStatus,
  parseInventoryDate,
  priorityForDaysUntilOtb,
} from "./inventoryStatus";

// Builds the Inventory dashboard aggregates from raw GET /inventory records.
//
// Each record is one sample. Its stage is the record's derivedStatus (worked
// out on fetch by getDerivedStatus, as the list shows it). For an earlier day
// (the overview timeline, week-on-week change) getDerivedStatus is run on the
// record as it was then: distribution dates after that day are dropped, and
// the undated task / photography counts only apply from the captured day.
// There's no field for "Returned", so it stays at 0.

// Shortest overview timeline, so a single-day range (e.g. Today) still has a
// line to draw; it ends on the selected day.
const MIN_OVERVIEW_DAYS = 7;

// Dashboard filter. Dates are "YYYY-MM-DD" strings so the filter can be kept
// in the (persisted, serialisable) store.
//   departments  category labels ("410 - Girls Footwear")
//   channels     channel names ("Unassigned" for samples with none)
//   statuses     SAMPLE_STATUSES, judged at each point in time
//   urgencies    URGENCY_LEVELS names (P0-P8, Low, Error), judged at each point in time
//   otbStart/otbEnd  inclusive OTB date range
export const EMPTY_DASHBOARD_FILTER = {
  departments: [],
  channels: [],
  statuses: [],
  urgencies: [],
  otbStart: "",
  otbEnd: "",
};

// The saved filter with defaults filled in. Urgencies saved before the P0-P8
// priorities (e.g. "Critical") are dropped rather than matching nothing.
const URGENCY_NAMES = URGENCY_LEVELS.map((level) => level.name);
const withFilterDefaults = (filter) => {
  const f = { ...EMPTY_DASHBOARD_FILTER, ...filter };
  return { ...f, urgencies: f.urgencies.filter((name) => URGENCY_NAMES.includes(name)) };
};

// Number of filter fields in use, for the filter button badge.
export const countDashboardFilters = (filter = {}) => {
  const f = withFilterDefaults(filter);
  return [
    f.departments.length,
    f.channels.length,
    f.statuses.length,
    f.urgencies.length,
    f.otbStart || f.otbEnd,
  ].filter(Boolean).length;
};
const TOP_CATEGORIES = 6;

const parseDate = (value, format) => {
  if (!value) return null;
  const date = format ? moment(value, format, true) : moment(value);
  return date.isValid() ? date : null;
};

const toNumber = (value) => {
  const number = parseInt(value, 10);
  return Number.isNaN(number) ? 0 : number;
};

// List fields arrive as arrays (sometimes nested), JSON array strings such as
// "[]", comma-separated strings or nothing at all; always return a flat array
// of non-empty values.
const toList = (value) => {
  if (Array.isArray(value)) return value.flatMap(toList);
  if (typeof value !== "string") {
    return value === null || value === undefined ? [] : [value];
  }
  const text = value.trim();
  if (text.startsWith("[")) {
    try {
      return toList(JSON.parse(text));
    } catch (err) {
      // Not JSON - fall through and treat it as a comma-separated string.
    }
  }
  // Malformed list text (e.g. "[a], ]" or "[\"a\"") - drop any brackets or
  // quotes left in the entries rather than showing them as labels.
  return text
    .split(",")
    .map((entry) => entry.replace(/[[\]"']/g, "").trim())
    .filter(Boolean);
};

const cleanText = (value) => (value === null || value === undefined ? "" : String(value).trim());

// Department code -> name, learnt from every record that has one. DeptName is
// often blank, so a code seen once with a name gets that name everywhere.
// Dept can also already be "160 - Fashion Accessories" (as on style masters).
const buildDepartmentNames = (records) =>
  records.reduce((names, item) => {
    const code = cleanText(item.Dept);
    const name = cleanText(item.DeptName);
    const combined = code.match(/^(\w+)\s*-\s*(.+)$/);
    if (combined) names[combined[1]] = names[combined[1]] ?? combined[2];
    else if (code && name) names[code] = names[code] ?? name;
    return names;
  }, {});

// "410 - Girls Footwear"; just the code when no name is known.
const departmentLabel = (item, names) => {
  const code = cleanText(item.Dept);
  const name = cleanText(item.DeptName);
  const combined = code.match(/^(\w+)\s*-\s*(.+)$/);
  if (combined) return `${combined[1]} - ${combined[2]}`;
  if (!code) return name || "Unassigned";
  const knownName = name || names[code];
  return knownName ? `${code} - ${knownName}` : `Dept ${code}`;
};

// Normalise a raw record once so the per-day passes below are cheap.
const normalise = (item, index, departmentNames) => {
  const distributionValues = toList(item.distributionDates);
  const distributions = distributionValues
    .map((value) => parseInventoryDate(value))
    .filter(Boolean)
    .sort((a, b) => a.valueOf() - b.valueOf());

  return {
    id: item.seasonSort || `#${index + 1}`,
    name: item.Name,
    // When the sample entered inventory: the record's created time (UTC).
    inAt: parseInventoryDate(item.created),
    // A shoot day counts as done once that day has started.
    shotAt: parseInventoryDate(item.shootDate),
    // When the images were captured; only used to time the undated counts.
    capturedAt: parseInventoryDate(item.captured),
    distributedAt: distributions[0] ?? null,
    // Has distribution dates, but none readable: still distributed (as the
    // list's derivedStatus says), just not on a known day.
    distributedUndated: distributionValues.length > 0 && !distributions.length,
    otb: parseInventoryDate(item.OTB),
    // Current status, as the list shows it.
    derivedStatus: item.derivedStatus ?? getDerivedStatus(item),
    record: item,
    distributions,
    // Session codes as a list, e.g. ["TAR-341190", "TAR-341194"].
    sessionCodes: toList(item.sessionCodes).map(cleanText).filter(Boolean),
    // Photographs (assets) taken of the sample.
    photographyCount: toNumber(item.photographyCount),
    category: departmentLabel(item, departmentNames),
    productCategory: cleanText(item.productCategory) || "Unassigned",
    productSubcategory: cleanText(item.productSubcategory) || "Unassigned",
    channels: [...new Set(toList(item.channels).map(canonicalChannel))],
    // Ids an errant scan may carry to point back at this sample.
    keys: [item.seasonSort, item.seasonPart, item.styleNumber, item.barcode, item.barCode]
      .map(cleanText)
      .filter(Boolean),
  };
};

// Channels always listed in the Channels panel (at 0 when none), named as in
// the app's shotChannels. Record values that only differ in case or
// punctuation ("ecommerce", "E-Commerce") are folded into these names.
const KNOWN_CHANNELS = ["e-commerce", "Packaging", "Campaign"];
const channelKey = (label) => String(label).toLowerCase().replace(/[^a-z0-9]/g, "");
const KNOWN_CHANNEL_BY_KEY = Object.fromEntries(
  KNOWN_CHANNELS.map((label) => [channelKey(label), label])
);
const canonicalChannel = (label) => KNOWN_CHANNEL_BY_KEY[channelKey(label)] ?? label;

const channelsOf = (sample) => (sample.channels.length ? sample.channels : ["Unassigned"]);

// Department and channel options for the filter panel, from the loaded records.
export const getDashboardFilterOptions = (records = []) => {
  const departmentNames = buildDepartmentNames(records);
  const samples = records.map((item, index) => normalise(item, index, departmentNames));
  const unique = (values) => [...new Set(values)].sort((a, b) => a.localeCompare(b));
  return {
    departments: unique(samples.map((sample) => sample.category)),
    channels: unique(samples.flatMap(channelsOf)),
  };
};

// Errant scans (GET /inventory/scans). The field names aren't confirmed yet,
// so the date and reason are read from the likely candidates.
const normaliseScan = (scan) => ({
  keys: [scan.seasonSort, scan.seasonPart, scan.styleNumber, scan.barcode, scan.barCode]
    .map(cleanText)
    .filter(Boolean),
  at:
    parseInventoryDate(scan.scanned) ??
    parseInventoryDate(scan.created) ??
    parseInventoryDate(scan.date),
  reason:
    scan.reason ||
    scan.errantReason ||
    scan.errorReason ||
    scan.error ||
    scan.message ||
    "Unknown",
});

const inInventoryAt = (sample, at) => !sample.inAt || !sample.inAt.isAfter(at);

// The sample's derivedStatus, or for a day before today, getDerivedStatus on
// the record as it stood at `at`.
const statusAt = (sample, at) => {
  if (!at.isBefore(moment().startOf("day"))) return sample.derivedStatus;
  const countsApply = !sample.capturedAt || !sample.capturedAt.isAfter(at);
  return getDerivedStatus({
    ...sample.record,
    // Undated distributions always count, as they do for derivedStatus.
    distributionDates: sample.distributedUndated
      ? sample.record.distributionDates
      : sample.distributions.filter((date) => !date.isAfter(at)),
    taskCount: countsApply ? sample.record.taskCount : 0,
    photographyCount: countsApply ? sample.record.photographyCount : 0,
  });
};

// Days until OTB -> URGENCY_LEVELS name (the list's priority label, P0-P8 /
// Low / Error). Distributed samples are measured at their distribution date
// (how close to OTB they went out), everything else at `at`.
const urgencyAt = (sample, at) => {
  if (!sample.otb) return "Error";
  const end =
    sample.distributedAt && !sample.distributedAt.isAfter(at) ? sample.distributedAt : at;
  const daysUntilOtb = sample.otb.clone().startOf("day").diff(end.clone().startOf("day"), "days");
  return priorityForDaysUntilOtb(daysUntilOtb).label;
};

const countBy = (values) =>
  values.reduce((counts, value) => {
    counts[value] = (counts[value] ?? 0) + 1;
    return counts;
  }, {});

const toVolumeList = (counts) =>
  Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);

// Always include `labels` (at 0 when missing), plus any other labels found;
// sorted by volume, with ties keeping the known order.
const withKnownLabels = (counts, labels) => {
  const extra = Object.keys(counts).filter((label) => !labels.includes(label));
  return [...labels, ...extra]
    .map((label, order) => ({ label, value: counts[label] ?? 0, order }))
    .sort((a, b) => b.value - a.value || a.order - b.order)
    .map(({ label, value }) => ({ label, value }));
};

// The largest TOP_CATEGORIES - 1 entries plus an "Other" total for the rest
// (flagged isOther, so a real category called "Other" isn't mistaken for it).
const topWithOther = (list) =>
  list.length > TOP_CATEGORIES
    ? [
        ...list.slice(0, TOP_CATEGORIES - 1),
        {
          label: "Other",
          isOther: true,
          value: list.slice(TOP_CATEGORIES - 1).reduce((sum, item) => sum + item.value, 0),
        },
      ]
    : list;

// Whole-number % change; undefined when there's nothing to compare against.
const percentChange = (current, previous) =>
  previous ? Math.round(((current - previous) / previous) * 100) : undefined;

const isWithin = (date, start, end) =>
  Boolean(date) && !date.isBefore(start) && date.isBefore(end);

/**
 * @param {Array} records  raw GET /inventory items
 * @param {Object} options
 * @param {string} options.weekStart  "YYYY-MM-DD" start of the selected range
 * @param {string} options.weekEnd    "YYYY-MM-DD" last day of the range
 *   (inclusive); a week from weekStart when not given
 * @param {Array}  options.scans      raw GET /inventory/scans items (errant scans)
 * @param {Object} options.filter     see EMPTY_DASHBOARD_FILTER
 * @param {boolean} options.recordsFiltered  records were already narrowed
 *   (e.g. by the list filter), so errant scans must link to one of them too
 */
export const buildInventoryDashboard = (
  records = [],
  { weekStart, weekEnd, scans = [], filter, recordsFiltered = false } = {}
) => {
  const f = withFilterDefaults(filter);
  const otbStart = parseDate(f.otbStart, "YYYY-MM-DD");
  const otbEnd = parseDate(f.otbEnd, "YYYY-MM-DD");

  // Fixed per sample, so applied once up front.
  const matchesRecord = (sample) =>
    (!f.departments.length || f.departments.includes(sample.category)) &&
    (!f.channels.length || channelsOf(sample).some((channel) => f.channels.includes(channel))) &&
    (!otbStart || (sample.otb && !sample.otb.isBefore(otbStart))) &&
    (!otbEnd || (sample.otb && !sample.otb.isAfter(otbEnd)));

  // Status and urgency change over time, so they're checked at each point the
  // dashboard looks at (a snapshot, or the end of a day/week of movements).
  const matchesAt = (sample, at) =>
    (!f.statuses.length || f.statuses.includes(statusAt(sample, at))) &&
    (!f.urgencies.length || f.urgencies.includes(urgencyAt(sample, at)));

  const departmentNames = buildDepartmentNames(records);
  const samples = records
    .map((item, index) => normalise(item, index, departmentNames))
    .filter(matchesRecord);

  // With a filter on, an errant scan only counts when it can be matched to a
  // sample that passes the filter at the time of the scan.
  const filtered = recordsFiltered || countDashboardFilters(f) > 0;
  const sampleByKey = new Map(samples.flatMap((sample) => sample.keys.map((key) => [key, sample])));
  const errantScans = scans.map(normaliseScan).filter((scan) => {
    if (!filtered) return true;
    const sample = scan.keys.map((key) => sampleByKey.get(key)).find(Boolean);
    return Boolean(sample) && matchesAt(sample, scan.at ?? moment());
  });

  // Samples whose `field` date falls in [start, end) and pass the filter at `at`.
  const movedWithin = (field, start, end, at) =>
    samples.filter((sample) => isWithin(sample[field], start, end) && matchesAt(sample, at))
      .length;
  const countScansWithin = (start, end) =>
    errantScans.filter((scan) => isWithin(scan.at, start, end)).length;

  const now = moment();
  const weekStartAt = parseDate(weekStart, "YYYY-MM-DD") ?? now.clone().startOf("isoWeek");
  // Exclusive end: the day after the range's last day.
  const lastDay = parseDate(weekEnd, "YYYY-MM-DD");
  const weekEndAt =
    lastDay && !lastDay.isBefore(weekStartAt)
      ? lastDay.clone().add(1, "day")
      : weekStartAt.clone().add(7, "days");
  const rangeDays = weekEndAt.diff(weekStartAt, "days");
  // "As of" the end of the selected range, but never in the future.
  const asOf = moment.min(weekEndAt.clone().subtract(1, "ms"), now);
  // The comparison period is the same length, just before the range.
  const previousAsOf = weekStartAt.clone().subtract(1, "ms");
  const previousWeekStartAt = weekStartAt.clone().subtract(rangeDays, "days");

  const snapshot = (at) => {
    const current = samples.filter(
      (sample) => inInventoryAt(sample, at) && matchesAt(sample, at)
    );
    const statuses = countBy(current.map((sample) => statusAt(sample, at)));
    return {
      total: current.length,
      inRetouching: statuses["Post Production"] ?? 0,
      distributed: statuses.Distributed ?? 0,
      // Unique session codes across the samples in inventory (a session
      // shared by several samples counts once).
      scheduled: new Set(current.flatMap((sample) => sample.sessionCodes)).size,
      // Total photographs across the samples in inventory.
      assets: current.reduce((sum, sample) => sum + sample.photographyCount, 0),
      statuses,
      current,
    };
  };

  const thisWeek = snapshot(asOf);
  const lastWeek = snapshot(previousAsOf);

  // Like checked in, errant scans are the selected week's movements.
  const errantScansThisWeek = errantScans.filter((scan) =>
    isWithin(scan.at, weekStartAt, weekEndAt)
  );

  const checkedIn = movedWithin("inAt", weekStartAt, weekEndAt, asOf);
  const checkedInLastWeek = movedWithin("inAt", previousWeekStartAt, weekStartAt, previousAsOf);

  // Daily overview: inventory is an end-of-day snapshot (the running total);
  // everything else is that day's movements - checked in (created), into
  // retouching (shoot day), distributed (first distribution) and errant scans.
  // The timeline covers the selected range (at least MIN_OVERVIEW_DAYS,
  // ending on its last day). Labels carry the year when it spans years.
  const overviewDays = Math.max(MIN_OVERVIEW_DAYS, rangeDays);
  const overviewStart = weekEndAt.clone().subtract(overviewDays, "days");
  const overviewFormat = overviewStart.isSame(weekEndAt.clone().subtract(1, "day"), "year")
    ? "MMM D"
    : "MMM D YYYY";
  const inventoryOverview = Array.from({ length: overviewDays }, (_, index) => {
    const dayStart = overviewStart.clone().add(index, "days");
    const dayEnd = dayStart.clone().add(1, "day");
    const dayAt = dayEnd.clone().subtract(1, "ms");
    const day = snapshot(dayAt);
    return {
      date: dayStart.format(overviewFormat),
      Inventory: day.total,
      "Checked In": movedWithin("inAt", dayStart, dayEnd, dayAt),
      Retouching: movedWithin("shotAt", dayStart, dayEnd, dayAt),
      Distributed: movedWithin("distributedAt", dayStart, dayEnd, dayAt),
      // Unique sessions across that day's inventory (a level, like the stat).
      Scheduled: day.scheduled,
      Assets: day.assets,
      "Errant Scans": countScansWithin(dayStart, dayEnd),
    };
  }).filter((day, index) => !overviewStart.clone().add(index, "days").isAfter(now));

  // Product categories, each with its subcategories for the panel's drill
  // down. "Other" rolls up the smaller categories and has no breakdown.
  const categories = topWithOther(
    toVolumeList(countBy(thisWeek.current.map((sample) => sample.productCategory)))
  ).map((item) =>
    item.isOther
      ? item
      : {
          ...item,
          children: topWithOther(
            toVolumeList(
              countBy(
                thisWeek.current
                  .filter((sample) => sample.productCategory === item.label)
                  .map((sample) => sample.productSubcategory)
              )
            )
          ),
        }
  );

  return {
    // Ids of the samples in view while a filter is on (null when not), so
    // panels built from other data (latest distributions) can follow it.
    sampleKeys: filtered ? new Set(thisWeek.current.flatMap((sample) => sample.keys)) : null,
    stats: {
      totalInventory: thisWeek.total,
      errantScans: errantScansThisWeek.length,
      checkedIn,
      inRetouching: thisWeek.inRetouching,
      distributed: thisWeek.distributed,
      scheduled: thisWeek.scheduled,
      assets: thisWeek.assets,
    },
    statChanges: {
      totalInventory: percentChange(thisWeek.total, lastWeek.total),
      errantScans: percentChange(
        errantScansThisWeek.length,
        countScansWithin(previousWeekStartAt, weekStartAt)
      ),
      checkedIn: percentChange(checkedIn, checkedInLastWeek),
      inRetouching: percentChange(thisWeek.inRetouching, lastWeek.inRetouching),
      distributed: percentChange(thisWeek.distributed, lastWeek.distributed),
      scheduled: percentChange(thisWeek.scheduled, lastWeek.scheduled),
      assets: percentChange(thisWeek.assets, lastWeek.assets),
    },
    inventoryOverview,
    urgencySamples: thisWeek.current.map((sample) => ({
      id: sample.id,
      name: sample.name,
      status: statusAt(sample, asOf),
      urgency: urgencyAt(sample, asOf),
      daysSinceScanned: sample.inAt
        ? Math.max(0, asOf.clone().startOf("day").diff(sample.inAt.clone().startOf("day"), "days"))
        : 0,
    })),
    // Every stage listed (in workflow order) so empty stages still show.
    itemsPerStage: SAMPLE_STATUSES.map((label) => ({
      label,
      value: thisWeek.statuses[label] ?? 0,
    })),
    categories,
    // A sample can go to several channels, so these can sum past the total.
    // e-commerce, Packaging and Campaign are always listed (at 0 if needed).
    channels: withKnownLabels(countBy(thisWeek.current.flatMap(channelsOf)), KNOWN_CHANNELS),
    // Every errant scan in the week has exactly one reason, so these sum to
    // stats.errantScans. Known reasons are always listed (at 0 if needed).
    errantReasons: withKnownLabels(
      countBy(errantScansThisWeek.map((scan) => scan.reason)),
      ERRANT_REASONS
    ),
  };
};
